import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Spinner, Alert, Card } from 'react-bootstrap';
import { FaSync } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/common/Navbar';
import Sidebar from '../components/common/Sidebar';
import EmployeeProfileCard from '../components/employee-dashboard/EmployeeProfileCard';
import AttendanceCalendarView from '../components/attendance/calendar/AttendanceCalendarView';
import EmployeeLeaveCard from '../components/employee-dashboard/EmployeeLeaveCard';
import EmployeeQuickActions from '../components/employee-dashboard/EmployeeQuickActions';
import { getAttendanceSummary, getMonthlyAttendance } from '../services/firebase/attendanceService';
import { getRequestsByEmployee } from '../wrms/services/wrmsService';
import { getEmployeeByEmail, getEmployeeByUid } from '../services/firebase/employeeService';

import '../assets/css/employee-dashboard.css';
import '../assets/css/dashboard.css';

const EmployeeDashboardPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [employeeProfile, setEmployeeProfile] = useState(null);
  const currentYear = new Date().getFullYear();

  // Get employee identifier
  const employeeId = user?.employeeCode || user?.employeeId || user?.id;

  // Get employee name for filtering
  const employeeName = user?.employeeName || user?.displayName ||
    `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '';

  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [pendingLeaves, setPendingLeaves] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    const uidToQuery = user?.uid || user?.id;
    if (!uidToQuery) {
      setError('Employee ID not found. Please contact admin to link your account.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch WRMS leave requests and employee profile data in parallel
      const [requests, uidEmpRes] = await Promise.all([
        getRequestsByEmployee(uidToQuery, 'leave').catch(() => []),
        getEmployeeByUid(uidToQuery).catch(() => null)
      ]);

      // Filter pending leaves
      setPendingLeaves(requests.filter(r => r.status === 'pending'));

      // If not found by UID, try by email
      if (uidEmpRes?.success && uidEmpRes?.data) {
        setEmployeeProfile(uidEmpRes.data);
      } else if (user?.email) {
        const emailEmpRes = await getEmployeeByEmail(user.email).catch(() => null);
        if (emailEmpRes?.success && emailEmpRes?.data) {
          setEmployeeProfile(emailEmpRes.data);
        }
      }

      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching employee dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    fetchDashboardData();
  };

  return (
    <div className="d-flex" style={{ width: '100%', overflowX: 'hidden' }}>
      <Sidebar />
      <div className="flex-grow-1 dashboard-main-content" style={{ background: '#f4f6f9', minHeight: '100vh', minWidth: 0 }}>
        <Navbar />
        <Container fluid className="py-3 py-md-4 px-2 px-md-4">
          {/* Header */}
          <Row className="mb-3 mb-md-4 g-2 align-items-center">
            <Col xs={8} md={8}>
              <h2 className="fw-bold text-dark mb-1 fs-4 fs-md-2">My Dashboard</h2>
              <small className="text-muted d-block text-truncate">
                Welcome back, {user?.employeeName || user?.firstName || 'Employee'}!
                <span className="d-none d-sm-inline"> · Last updated: {lastRefresh.toLocaleTimeString()}</span>
              </small>
            </Col>
            <Col xs={4} md={4} className="d-flex justify-content-end align-items-center">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleRefresh}
                disabled={loading}
              >
                <FaSync className={loading ? 'fa-spin' : ''} /> <span className="d-none d-sm-inline ms-1">Refresh</span>
              </Button>
            </Col>
          </Row>

          {/* Error Alert */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading your dashboard...</p>
            </div>
          ) : (
            <Row className="g-3">
              {/* Profile and Quick Actions on Top Row */}
              <Col lg={8} className="mb-2 mb-lg-4">
                {(() => {
                  const mergedUser = {
                    ...user,
                    ...(employeeProfile ? {
                      photoURL: employeeProfile.personal?.photo || employeeProfile.photoURL || employeeProfile.photo || employeeProfile.personalInfo?.photo || user?.photoURL || user?.photo,
                      photo: employeeProfile.personal?.photo || employeeProfile.photoURL || employeeProfile.photo || employeeProfile.personalInfo?.photo || user?.photoURL || user?.photo,
                      employeeName: employeeProfile.personal?.fullName || `${employeeProfile.personal?.firstName || ''} ${employeeProfile.personal?.lastName || ''}`.trim() || user?.employeeName,
                      designation: employeeProfile.job?.designation || user?.designation,
                      department: employeeProfile.job?.department || user?.department,
                      employeeCode: employeeProfile.job?.employeeCode || employeeProfile.employeeCode || user?.employeeCode,
                      joiningDate: employeeProfile.job?.joiningDate || user?.joiningDate,
                      employmentType: employeeProfile.job?.employmentType || user?.employmentType
                    } : {})
                  };
                  return <EmployeeProfileCard user={mergedUser} />;
                })()}
              </Col>
              <Col lg={4} className="mb-2 mb-lg-4">
                <EmployeeQuickActions />
              </Col>

              {/* Full Width Attendance Overview below */}
              <Col lg={12} className="mb-3 mb-lg-4">
                <Card className="border-0 shadow-sm overflow-hidden rounded-4">
                  <Card.Header className="bg-white p-3 p-md-4 border-0">
                    <h4 className="fw-bold mb-0 fs-5 fs-md-4">My Attendance Overview</h4>
                  </Card.Header>
                  <Card.Body className="px-2 px-md-4 pb-3 pb-md-4 pt-0">
                    <AttendanceCalendarView
                      initialEmployeeId={employeeId}
                      hideEmployeeSelector={true}
                      isDashboard={true}
                      showDashboardStats={true}
                    />
                  </Card.Body>
                </Card>
              </Col>

              {/* Leave Card full width below */}
              <Col lg={12} className="mb-4">
                <EmployeeLeaveCard
                  pendingLeaves={pendingLeaves}
                  loading={loading}
                />
              </Col>
            </Row>
          )}
        </Container>
      </div>
    </div>
  );
};

export default EmployeeDashboardPage;
